import React, { useEffect, useState } from "react";
import styled from "styled-components";
import axios from "axios";
import { useMutation } from "@tanstack/react-query";
import {
  Center,
  Text,
  Button,
  Group,
  Stack,
  Card,
  Box,
  Flex,
  TextInput,
} from "@mantine/core";
import toast from "react-hot-toast";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import ViewTitle from "./ViewTitle";
import useAppStore from "./microsite-store";
import {
  StyledImage,
  StyledPlaceholder,
} from "@components/RideChallengeApp/UserHistory";
import ViewLoader from "./ViewLoader";
export default function PreferredLocation({ onSubmit = null, children }) {
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [showSelect, setShowSelect] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locations, setLocations] = useState([]);
  const [mode, setMode] = useState("curated");
  const currentPreferredLocation = useAppStore(
    (state) => state.preferredLocation
  );
  const user = useAppStore((state) => state.user);
  const entityInfo = useAppStore((state) => state.entityInfo);
  const location = useAppStore((state) => state.location);
  const setPreferredLocation = useAppStore(
    (state) => state.setPreferredLocation
  );
  const { t } = useTranslation();

  if (!entityInfo.organization_id) return <div>{children}</div>;

  useEffect(() => {
    if (currentPreferredLocation) {
      setLoadingLocations(false);
      setShowSelect(false);
      return;
    } else {
      setShowSelect(true);
    }
  }, []);

  useEffect(() => {
    if (showSelect) {
      loadLocations();
    }
  }, [showSelect]);

  function loadLocations() {
    setLoadingLocations(true);
    axios
      .post("/user-preferred-locations/retrieve-locations-geo/", {
        user_id: user.id,
        organization_id: entityInfo.organization_id,
        zip: true,
      })
      .then(({ data }) => {
        loadLocations2(
          data.response
            .sort((a, b) => a.distance - b.distance)
            .map((m) => ({
              ...m,
              group: "distance",
            }))
        );
      })
      .catch((err) => {
        //
        loadLocations2();
      });
  }

  function loadLocations2(locationsFromGeo = []) {
    axios
      .post(`/user-preferred-locations/retrieve-frequently-visited/`, {
        user_id: user.id,
        organization_id: entityInfo.organization_id,
      })
      .then(({ data }) => {
        const frequentlyVisitedLocations = data.response.map((m) => ({
          ...m,
          group: "frequently_visited",
        }));
        setLocations([
          ...locationsFromGeo.filter((f) =>
            frequentlyVisitedLocations.some((g) => g.id !== f.id)
          ),
          ...frequentlyVisitedLocations,
        ]);
        // setLocations();
      })
      .catch((err) => {
        //
        setLocations([...locationsFromGeo]);
      })
      .finally(() => {
        setLoadingLocations(false);
      });
  }

  function onPreferredLocationSaveSuccess(loc) {
    setPreferredLocation(loc);
    if (onSubmit) {
      onSubmit(loc);
    }
    toast.success(t("common:saved") + "!");
  }

  const savePreferredLocationMutation = useMutation({
    mutationFn: async (location) => {
      if (!user) {
        throw new Error("User not found");
      }
      return axios.post(`/microsite/update-preferred-location/`, {
        organization_id: entityInfo.organization_id,
        location_id: location.id,
        user_id: user.id,
      });
    },
    onSuccess: (data, location) => {
      setPreferredLocation(location);
      if (onSubmit) {
        onSubmit(location);
      }
      toast.success(t("common:saved") + "!");
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.error?.message ||
          err?.message ||
          "Failed to save home dealership"
      );
    },
  });

  function onSave() {
    if (!selectedLocation) return;
    savePreferredLocationMutation.mutate(selectedLocation);
  }

  if (loadingLocations) {
    return (
      <div>
        <ViewLoader />
      </div>
    );
  }

  const locationsToShow = selectedLocation ? [selectedLocation] : locations;
  const canEditPreferredLocation =
    location &&
    location.present_at_location &&
    location.id !== currentPreferredLocation?.id;
  const locationGroups = [
    {
      title: t("microsite:preferred_location_group_current_location"),
      locations: [location].filter((f) => f.present_at_location),
    },
    {
      title: t(
        "microsite:preferred_location_group_frequently_visited_locations"
      ),
      locations: locations
        .filter((f) => f.group === "frequently_visited")
        .filter((f) =>
          location.present_at_location ? f.id !== location?.id : true
        ),
    },
    {
      title: t("microsite:preferred_location_group_locations_near_zip_code"),
      locations: locations
        .filter((f) => f.group === "distance")
        .filter((f) =>
          location.present_at_location ? f.id !== location?.id : true
        ),
    },
  ].filter((f) => f.locations.length > 0);

  if (showSelect) {
    return (
      <div>
        <ViewTitle
          title={t("microsite:preferred_location_title")}
          subtitle={t("microsite:preferred_location_subtitle")}
          marginBottom="1em"
        />
        {!selectedLocation && locationsToShow.length === 0 && (
          <>
            <Text align="center" c="dimmed" mb="xl">
              {t("microsite:preferred_location_no_locations_available")}
            </Text>
          </>
        )}
        {selectedLocation ? (
          <>
            <ConfirmLocationSelection
              reqData={{
                user_id: user.id,
                organization_id: entityInfo.organization_id,
              }}
              location={selectedLocation}
              onSuccess={(loc) => {
                onPreferredLocationSaveSuccess(loc);
              }}
              onCancel={() => setSelectedLocation(null)}
            >
              <Flex justify="center" gap="xs" mt="lg">
                <Button
                  c="dark"
                  color="gray.1"
                  size="xs"
                  radius="lg"
                  onClick={() => setSelectedLocation(null)}
                  mt="sm"
                >
                  {t("microsite:back_button")}
                </Button>
              </Flex>
            </ConfirmLocationSelection>
          </>
        ) : (
          <>
            {mode === "curated" && (
              <>
                <Stack>
                  {locationGroups.map((group, index) => (
                    <div>
                      <LocationGroup key={index} title={group.title}>
                        <Stack gap={5}>
                          {group.locations.map((location) => (
                            <LocationItem
                              key={location.id}
                              location={location}
                              onLocationSelect={() =>
                                selectedLocation
                                  ? null
                                  : setSelectedLocation(location)
                              }
                              isSelected={selectedLocation?.id === location.id}
                            />
                          ))}
                        </Stack>
                      </LocationGroup>
                    </div>
                  ))}
                </Stack>
                <Flex justify="center" my="lg">
                  <Button
                    variant="subtle"
                    color="gray"
                    size="xs"
                    onClick={() =>
                      setMode(mode === "curated" ? "search" : "curated")
                    }
                  >
                    {mode === "curated"
                      ? t(
                          "microsite:preferred_location_switch_to_search_mode_button"
                        )
                      : t("microsite:back")}
                  </Button>
                </Flex>
              </>
            )}
            {mode === "search" && (
              <>
                <Box>
                  <LocationSearch
                    onPreferredLocationSaveSuccess={
                      onPreferredLocationSaveSuccess
                    }
                  >
                    <Flex justify="center" mt="lg">
                      <Button
                        variant="subtle"
                        color="gray"
                        size="xs"
                        onClick={() =>
                          setMode(mode === "curated" ? "search" : "curated")
                        }
                      >
                        {mode === "curated"
                          ? t(
                              "microsite:preferred_location_switch_to_search_mode_button"
                            )
                          : t("microsite:back")}
                      </Button>
                    </Flex>
                  </LocationSearch>
                </Box>
              </>
            )}
            {currentPreferredLocation && (
              <Flex justify="center" gap="xs">
                <Button
                  c="dark"
                  color="gray.1"
                  size="xs"
                  radius="lg"
                  onClick={() => setShowSelect(false)}
                >
                  {t("microsite:back_button")}
                </Button>
              </Flex>
            )}
          </>
        )}
      </div>
    );
  }

  if (showEdit) {
    return (
      <div>
        <ViewTitle
          title={t("microsite:preferred_location_title")}
          subtitle={t("microsite:preferred_location_subtitle")}
          marginBottom="1em"
        />
        {currentPreferredLocation && selectedLocation && (
          <>
            <Text align="center" c="dimmed" mb="lg" maw="60%" mx="auto">
              {t("microsite:preferred_location_current_preferred_location")}
              <br />
              <b>{currentPreferredLocation?.name}</b>
            </Text>
            <Text align="center" c="dimmed" mb="sm" maw="80%" mx="auto">
              {t("microsite:preferred_location_edit_confirmation")}
            </Text>
          </>
        )}
        <Stack gap={5}>
          {[{ ...location }].map((location) => (
            <LocationItem
              key={location.id}
              location={location}
              onLocationSelect={() =>
                selectedLocation ? null : setSelectedLocation(location)
              }
              isSelected={selectedLocation?.id === location.id}
            />
          ))}
          {!selectedLocation && !currentPreferredLocation && (
            <Flex justify="center" mt="lg">
              {children}
            </Flex>
          )}
          {selectedLocation && (
            <>
              <Button
                disabled={!selectedLocation}
                loading={savePreferredLocationMutation.isPending}
                fullWidth
                onClick={onSave}
                size="md"
              >
                {t("microsite:preferred_location_save_button")}
              </Button>
              <Flex justify="center" gap="xs">
                <Button
                  c="dark"
                  color="gray.1"
                  size="xs"
                  radius="lg"
                  onClick={() => setSelectedLocation(null)}
                  mt="sm"
                >
                  {t("microsite:back_button")}
                </Button>
              </Flex>
            </>
          )}
          {!selectedLocation && currentPreferredLocation && (
            <Flex justify="center" gap="xs">
              <Button
                c="dark"
                color="gray.1"
                size="xs"
                radius="lg"
                onClick={() => setShowEdit(false)}
              >
                {t("microsite:back_button")}
              </Button>
            </Flex>
          )}
        </Stack>
      </div>
    );
  }

  if (currentPreferredLocation) {
    return (
      <div>
        <ViewTitle
          title={t("microsite:preferred_location_title")}
          marginBottom="1em"
        />
        <LocationItem location={currentPreferredLocation} isSelected={true} />
        <Flex justify="center" gap="xs" mt="lg">
          {canEditPreferredLocation && (
            <Button
              c="dark"
              color="gray.1"
              size="xs"
              radius="lg"
              onClick={() => setShowEdit(true)}
            >
              {t("microsite:change")}
            </Button>
          )}
          {children}
        </Flex>
      </div>
    );
  }

  return (
    <div>
      <ViewTitle
        title={t("microsite:preferred_location_title")}
        subtitle={t("microsite:preferred_location_subtitle")}
      />
      {location && (
        <Center>
          <Text size="xl" mb="xl" align="center" c="white">
            {t("microsite:is_location_your_home_dealership", {
              location: location.name,
            })}
          </Text>
        </Center>
      )}
      <Group grow>
        <Button onClick={() => setPreferredLocation(location)} size="xl">
          {t("microsite:yes")}
        </Button>
        <Button
          color="red"
          onClick={() => setShowSelect(true)}
          variant="light"
          size="xl"
        >
          {t("microsite:no")}
        </Button>
      </Group>
    </div>
  );
}

const LocationItem = ({ location, onLocationSelect, isSelected }) => {
  const imageSize = isSelected ? 125 : 40;
  const { t } = useTranslation();
  const animationVariants = {
    initial: {
      opacity: 0,
      scale: 0.8,
      y: 20,
    },
    animate: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1],
      },
    },
  };

  if (isSelected) {
    return (
      <motion.div
        initial="initial"
        animate="animate"
        variants={animationVariants}
        onClick={onLocationSelect}
        style={{ cursor: "pointer" }}
      >
        <CardContainer>
          <Stack>
            <Flex justify="center">
              {location.logo_url ? (
                <StyledImage
                  height={imageSize}
                  width={imageSize}
                  src={location.logo_url}
                />
              ) : (
                <StyledPlaceholder height={imageSize} width={imageSize} />
              )}
            </Flex>
            <div>
              <Text size="lg" align="center" fw={600} c="white">
                {location.name}
              </Text>
              <Text size="md" align="center" c="dimmed">
                {location.city}, {location.state_province}
              </Text>
            </div>
          </Stack>
        </CardContainer>
      </motion.div>
    );
  }

  return (
    <div onClick={onLocationSelect} style={{ cursor: "pointer" }}>
      <CardContainer>
        <Flex gap="sm" align="flex-start">
          {location.logo_url ? (
            <StyledImage
              height={imageSize}
              width={imageSize}
              src={location.logo_url}
            />
          ) : (
            <StyledPlaceholder height={imageSize} width={imageSize} />
          )}
          <div style={{ flexGrow: 1 }}>
            <Text size="sm" fw={600} c="white">
              {location.name}
            </Text>
            <Text size="sm" c="dimmed">
              {location.city}, {location.state_province}
            </Text>
            {location.distance && (
              <Text size="sm" c="dimmed">
                {location.distance}{" "}
                {t("microsite:preferred_location_miles_away")}
              </Text>
            )}
            {isSelected && (
              <Text size="xs" fw={600} c="green">
                {t("microsite:selected")}
              </Text>
            )}
          </div>
        </Flex>
      </CardContainer>
    </div>
  );
};

const CardContainer = ({ children }) => (
  <Card
    mb="xs"
    style={{
      background: "var(--mantine-color-dark-8)",
      // borderWidth: 4,
      borderColor: "var(--mantine-color-dark-5)",
    }}
  >
    {children}
  </Card>
);

const LocationGroup = ({ children, title }) => {
  return (
    <div>
      <Text align="center" mb="sm" size="sm" c="dimmed" w="80%" mx="auto">
        {title}
      </Text>
      {children}
    </div>
  );
};

const LocationSearch = ({ children, onPreferredLocationSaveSuccess }) => {
  const [formValues, setFormValues] = useState({
    zip: "",
  });
  const [locations, setLocations] = useState([]);
  const [draftLocation, setDraftLocation] = useState(null);
  const entityInfo = useAppStore((state) => state.entityInfo);
  const user = useAppStore((state) => state.user);
  const { t } = useTranslation();

  const searchMutation = useMutation({
    mutationFn: async (zip) => {
      return axios.post(`/user-preferred-locations/retrieve-locations-geo/`, {
        zip: true,
        zipcode: zip.trim(),
        organization_id: entityInfo.organization_id,
        // campaign_effort_id: entityInfo.campaign_effort_id,
        user_id: user?.id,
      });
    },
    onSuccess: ({ data }) => {
      if (data.response.length === 0) {
        toast.error(t("microsite:no_locations_found"));
        return;
      }
      setLocations(data.response);
    },
    onError: (err) => {
      toast.error(err || t("microsite:failed_to_search_locations"));
    },
  });

  const searchDisabled = formValues.zip.length < 5 || formValues.zip.length > 7;

  if (draftLocation) {
    return (
      <div>
        <ConfirmLocationSelection
          reqData={{
            user_id: user.id,
            organization_id: entityInfo.organization_id,
          }}
          location={draftLocation}
          onSuccess={() => onPreferredLocationSaveSuccess(draftLocation)}
          onCancel={() => setDraftLocation(null)}
        >
          <Flex justify="center" gap="xs" mt="lg">
            <Button
              variant="subtle"
              color="gray"
              size="xs"
              onClick={() => setDraftLocation(null)}
            >
              {t("microsite:back_button")}
            </Button>
          </Flex>
        </ConfirmLocationSelection>
      </div>
    );
  }

  return (
    <div>
      {locations.length > 0 ? (
        <>
          <Stack gap={5}>
            <Text align="center" mb="sm" size="sm" c="dimmed" w="80%" mx="auto">
              {t("microsite:preferred_location_search_results_title", {
                zipcode: formValues.zip,
              })}
            </Text>
            {locations.map((location) => (
              <LocationItem
                key={location.id}
                location={location}
                onLocationSelect={() => setDraftLocation(location)}
              />
            ))}
            <Flex justify="center">
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                onClick={() => {
                  setFormValues({ ...formValues, zip: "" });
                  setLocations([]);
                }}
              >
                {t("microsite:preferred_location_change_zip_button")}
              </Button>
            </Flex>
          </Stack>
        </>
      ) : (
        <>
          <Stack gap="sm">
            <TextInput
              size="lg"
              value={formValues.zip}
              onChange={(e) =>
                setFormValues({ ...formValues, zip: e.target.value })
              }
              placeholder={t(
                "microsite:preferred_location_search_by_zip_placeholder"
              )}
              styles={{
                input: {
                  textAlign: "center",
                },
              }}
            />
            <Button
              loading={searchMutation.isPending}
              disabled={searchDisabled}
              size="lg"
              onClick={() => searchMutation.mutate(formValues.zip)}
            >
              {t("microsite:preferred_location_search_button")}
            </Button>
          </Stack>
          {children}
        </>
      )}
    </div>
  );
};

const ConfirmLocationSelection = ({
  location,
  onSuccess,
  reqData = {},
  children,
}) => {
  const { t } = useTranslation();

  const savePreferredLocationMutation = useMutation({
    mutationFn: async () => {
      if (!reqData.user_id) {
        throw new Error("User not found");
      }
      return axios.post(`/microsite/update-preferred-location/`, {
        ...reqData,
        location_id: location.id,
      });
    },
    onSuccess: () => {
      onSuccess(location);
    },
    onError: (err) => {
      toast.error(
        err?.response?.data?.error?.message ||
          err?.message ||
          t("microsite:failed_to_save_preferred_location")
      );
    },
  });

  return (
    <div>
      <>
        <LocationItem
          location={location}
          onLocationSelect={() => null}
          isSelected={true}
        />
        <Button
          disabled={!location}
          loading={savePreferredLocationMutation.isPending}
          fullWidth
          onClick={() => savePreferredLocationMutation.mutate()}
          size="md"
          mt="md"
        >
          {t("microsite:preferred_location_save_button")}
        </Button>
        <>{children}</>
      </>
    </div>
  );
};
